import { HTMLMotionProps, motion, useInView } from 'framer-motion';
import { useCallback, useRef, useState } from 'react';

const variants = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: { duration: 0.2, ease: [0.42, 0, 0.01, 1] },
  },
};

export interface IAsyncImageProps
  extends Omit<HTMLMotionProps<'img'>, 'onLoad'> {
  alt?: string;
  /** Should the image fade in as it scrolls into view? (default: `true`) */
  animateOnScroll?: boolean;
  className?: string;
  onLoad?: (img: HTMLImageElement) => void;
}

export const AsyncImage = ({
  alt = '',
  animateOnScroll = true,
  className = '',
  onLoad = undefined,
  ...rest
}: IAsyncImageProps) => {
  const imageRef = useRef<HTMLImageElement>();
  const [isLoaded, setIsLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const isInView = useInView(imageRef);

  if (!isVisible && isLoaded) {
    if ((animateOnScroll && isInView) || !animateOnScroll) {
      setIsVisible(true);
    }
  }

  const handleLoad = (img: HTMLImageElement) => {
    setIsLoaded(true);
    onLoad?.(img);
  };

  const setImageRef = useCallback((img: HTMLImageElement) => {
    if (img) {
      if (img.complete) {
        handleLoad(img);
      } else {
        img.onload = () => handleLoad(img);
      }
      imageRef.current = img;
    }
  }, []);

  return (
    <motion.img
      alt={alt}
      animate={isVisible && 'animate'}
      className={className}
      initial="initial"
      variants={variants}
      {...rest}
      ref={setImageRef}
    />
  );
};
